import { AttentionDot } from '@components/ui/AttentionDot';
import { cn } from '@lib/utils';

export interface LiveBadgeProps {
  /** `on-image` floats on a cover photo (adds elevation); `inline` sits in rows. */
  variant?: 'on-image' | 'inline';
  /** `screen`: the big screen's pill (TECH-867 B1b–B2c: 20px label, 12px dot, 1.5px border). */
  size?: 'default' | 'screen';
}

/**
 * White live pill: pulsing `mb-live` dot + "Live" (locked copy, spec §4 —
 * the Figma white-pill look, not the old solid-red badge).
 *
 * L9 reservation: this red-dot-plus-"Live" pair means an Event or a live
 * poll happening now (an Event's `start ≤ now < end`; a live poll from Start
 * to End, TECH-867 § Decided) and nothing else in the app may use it; liveness is state, never an unread
 * count. The dot pulse is a live-layer looping animation (§4.1): opacity
 * 1→0.45→1 over 2s, GPU-only, static (still visible) under
 * prefers-reduced-motion. (It and the space-slot content-row rotation — W2,
 * Dexter-amended §4.1 — are the layer's only persistent motions; v1's
 * pulse-only rule was widened for that rotation.)
 */
export function LiveBadge({ variant = 'inline', size = 'default' }: LiveBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex w-fit shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-gray-900',
        variant === 'on-image' && 'shadow-soft',
        // Inline sits on near-white surfaces (mb-offwhite rows) where the white
        // pill would dissolve — a hairline inset ring (LivePill's gray-100 edge
        // token) keeps the boundary without shifting layout (W1-3 M7a).
        variant === 'inline' && 'ring-1 ring-gray-100 ring-inset',
        size === 'screen' &&
          'gap-2 px-3.5 py-1.5 font-fira text-[20px] leading-6 text-mb-ink ring-[1.5px] ring-mb-border',
      )}
    >
      <AttentionDot
        className={cn(
          'bg-mb-live animate-live-pulse motion-reduce:animate-none',
          size === 'screen' && 'h-3 w-3',
        )}
      />
      Live
    </span>
  );
}
